import { useState, useRef, useEffect } from 'react';
import { t } from '../lib/i18n';
import { useAuth, type Membership, type Role, type TenantLevel } from '../lib/auth-context';

/**
 * F1-019 — Dropdown de troca de tenant.
 *
 * - Lista memberships do auth-context
 * - Click troca via switchTenant() (POST /auth/switch-tenant)
 * - Indica "voce esta em outro espaco" quando active != primeiro membership
 * - 403 (tenant scope mismatch) e tratado no auth-context e expoe flash de
 *   lostTenant — o AppLayout exibe o toast.
 */

function levelLabel(level: TenantLevel): string {
  if (level === 'root') return t('tenants.switcher.levelRoot');
  if (level === 'intermediate') return t('tenants.switcher.levelIntermediate');
  return t('tenants.switcher.levelLeaf');
}

function roleLabel(role: Role): string {
  switch (role) {
    case 'owner':
      return t('tenants.switcher.roleOwner');
    case 'admin':
      return t('tenants.switcher.roleAdmin');
    case 'editor':
      return t('tenants.switcher.roleEditor');
    case 'member':
      return t('tenants.switcher.roleMember');
    case 'viewer':
      return t('tenants.switcher.roleViewer');
    case 'api_consumer':
      return t('tenants.switcher.roleApiConsumer');
  }
}

function membershipDisplayName(m: Membership): string {
  if (m.tenant_name !== undefined && m.tenant_name.length > 0) {
    return m.tenant_name;
  }
  return m.tenant_id.slice(0, 8);
}

export function TenantSwitcher(): JSX.Element | null {
  const { memberships, activeTenantId, switchTenant, isAuthenticated } = useAuth();
  const [open, setOpen] = useState(false);
  const [switching, setSwitching] = useState<string | null>(null);
  const [localError, setLocalError] = useState<string | null>(null);
  const rootRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!open) return;
    const onDocClick = (ev: MouseEvent): void => {
      if (
        rootRef.current !== null &&
        ev.target instanceof Node &&
        !rootRef.current.contains(ev.target)
      ) {
        setOpen(false);
      }
    };
    const onEsc = (ev: KeyboardEvent): void => {
      if (ev.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onEsc);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onEsc);
    };
  }, [open]);

  if (!isAuthenticated) return null;
  if (memberships.length === 0) return null;

  const active = memberships.find((m) => m.tenant_id === activeTenantId) ?? null;
  const first = memberships[0];
  const inElsewhere =
    first !== undefined && activeTenantId !== null && first.tenant_id !== activeTenantId;

  const handleSwitch = async (tenantId: string): Promise<void> => {
    if (tenantId === activeTenantId) {
      setOpen(false);
      return;
    }
    setLocalError(null);
    setSwitching(tenantId);
    try {
      await switchTenant(tenantId);
      setOpen(false);
    } catch {
      setLocalError(t('tenants.switcher.switchFailed'));
    } finally {
      setSwitching(null);
    }
  };

  return (
    <div className="tenant-switcher" ref={rootRef}>
      <button
        type="button"
        className="tenant-switcher-trigger"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={t('tenants.switcher.open')}
        onClick={() => setOpen((v) => !v)}
      >
        <span className="tenant-switcher-label">{t('tenants.switcher.label')}</span>
        <span className="tenant-switcher-current">
          {active !== null ? membershipDisplayName(active) : '—'}
        </span>
        {inElsewhere ? (
          <span className="tenant-switcher-elsewhere" aria-live="polite">
            {t('tenants.switcher.elsewhereBadge')}
          </span>
        ) : null}
      </button>

      {open ? (
        <ul className="tenant-switcher-list" role="listbox" aria-label={t('tenants.switcher.open')}>
          {memberships.map((m) => {
            const isActive = m.tenant_id === activeTenantId;
            const isSwitching = switching === m.tenant_id;
            return (
              <li key={m.tenant_id} role="option" aria-selected={isActive}>
                <button
                  type="button"
                  disabled={isSwitching}
                  onClick={() => {
                    void handleSwitch(m.tenant_id);
                  }}
                >
                  <span className="tenant-name">{membershipDisplayName(m)}</span>
                  <span className="tenant-meta">
                    {levelLabel(m.tenant_level)} · {roleLabel(m.role)}
                  </span>
                  {isActive ? (
                    <span className="tenant-current-dot" aria-hidden="true">
                      •
                    </span>
                  ) : null}
                  {isSwitching ? <span>{t('tenants.switcher.switching')}</span> : null}
                </button>
              </li>
            );
          })}
          {localError !== null ? (
            <li role="alert" className="error">
              {localError}
            </li>
          ) : null}
        </ul>
      ) : null}
    </div>
  );
}
